import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Box, Button, List, ListItem, TextField, Typography, Alert} from '@mui/material';
import {IconButton} from '@mui/material';
import {CloseTwoTone, ErrorOutline} from '@mui/icons-material';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import * as elemental_axios_client from '../../../util/elemental_axios_client';
import {enqueueSnackbar} from 'notistack';
import {AudienceSelector} from '../Audiences';
import MutationLoadingButton from 'components/MutationLoadingButton';
import VariantTestCard from './VariantTestCard';

const SendTestSidebar = ({ message }) => {

    const [emails, setEmails] = useState([]);
    const [currentEmail, setCurrentEmail] = useState('');
    const [error, setError] = useState('');
    const [successfulEmails, setSuccessfulEmails] = useState([]);

    const [selectedAudiences, setSelectedAudiences] = useState([]);
    const [showAddRecipients, setShowAddRecipients] = useState(false);

    const queryClient = useQueryClient();

    const { data: allAudiences, isLoading: isLoadingAudiences , isSuccess: isSuccessAudiences } = useQuery({
        queryKey: ['audiences'],
        queryFn: () => {
            return elemental_axios_client.get('/audiences?per_page=1000');
        },
        select: (data) => {
            return data['page'];
        }
    });

    const { data: testVariants, isLoading: isLoadingVariants } = useQuery({
        queryKey: ['message-test-variants', message.id],
        queryFn: () => {
            return elemental_axios_client.get(`/messages/email/${message.id}/variants/tests`);
        }
    });

    const testAudiences = allAudiences?.filter(audience => audience.is_test);

    const addEmail = () => {
        if(/\S+@\S+\.\S+/.test(currentEmail)) {
            if (emails.includes(currentEmail)) {
                setError('Email already added');
                return;
            }
            setEmails(emailAddresses => [...emailAddresses, currentEmail]);
            setCurrentEmail('');
            setError('');
            return;
        }
        setError('Not a valid email address');
    };

    const removeEmail = (emailToRemove) => {
        setEmails(emailAddresses => emailAddresses.filter((email) => emailToRemove !== email));
    };

    const sendTestMutation = useMutation( {
        mutationFn: ( data) => {
            return elemental_axios_client.post(`/messages/${message.id}/send_test`, data);
        },
        onSuccess: (data) => {
            enqueueSnackbar('Test sent', { variant: 'success' });
            queryClient.invalidateQueries(['message-test-variants', message.id]);
        },
        onError: (error, variables, context) => {
            console.log(error.response.data);
            enqueueSnackbar('Problem sending test', { variant: 'error' });
        }
    });

    const sendTest = async () => {
        const payload = {
            segment_ids: selectedAudiences.map(audience => audience.id),
            emails: emails
        };

        if (payload.segment_ids.length === 0 && payload.emails.length === 0) {
            setError('No recipients selected');
            return;
        }

        sendTestMutation.mutate(payload);
    };

    // TODO: useful for reloading the inputs the next time w/o retyping
    // const saveTestDataToStorage = () => {
    //     localStorage.setItem(`sendTest-${message.id}`, JSON.stringify(currentTestData.current));
    // };
    //
    // const parseDataFromStorage = () => {
    //     const testState = JSON.parse(localStorage.getItem(`sendTest-${message.id}`));
    //     if (testState) {
    //         setEmails(testState.emails);
    //         setPreSelectedRowIds(testState.selectedRowIds);
    //     }
    // };


    return (
        <Box className='flow-sidebar' sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box className='flow-sidebar__body' sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box className='sidebar-send-test' sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>

                    {sendTestMutation.isSuccess &&
                        <Box sx={{ mt: 2, mb: 2, flexShrink: 0 }}>
                            {sendTestMutation.data.emails.length > 0 &&
                                <Alert severity="success" sx={{ mb: 2 }}>
                                    <Typography variant='subtitle2' sx={{ mb: 1 }}>Successfully Initiated Test To:</Typography>
                                    <List dense>
                                        {sendTestMutation.data.emails.sort((a, b) => a.localeCompare(b)).map(email => <ListItem key={email}>{email}</ListItem>)}
                                    </List>
                                </Alert>
                            }
                            {sendTestMutation.data.emails_suppressed?.length > 0 &&
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    <Typography variant='subtitle2' sx={{ mb: 1 }}>Suppressed Emails (not sent):</Typography>
                                    <List dense>
                                        {sendTestMutation.data.emails_suppressed.sort((a, b) => a.localeCompare(b)).map(email => <ListItem key={email}>{email}</ListItem>)}
                                    </List>
                                </Alert>
                            }
                            {sendTestMutation.data.emails_invalid?.length > 0 &&
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    <Typography variant='subtitle2' sx={{ mb: 1 }}>Invalid Emails:</Typography>
                                    <List dense>
                                        {sendTestMutation.data.emails_invalid.sort((a, b) => a.localeCompare(b)).map(email => <ListItem key={email}>{email}</ListItem>)}
                                    </List>
                                </Alert>
                            }
                        </Box>
                    }





                    {sendTestMutation.isError &&
                        <Box sx={{ mt: 2, mb: 2, flexShrink: 0 }}>
                            <Alert severity="error" sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Problem Sending Test</Typography>
                                
                                {sendTestMutation.error?.response?.data?.variants_not_sent?.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                            {sendTestMutation.error.response.data.variants_not_sent.length} variant(s) could not be sent:
                                        </Typography>
                                        <List dense>
                                            {sendTestMutation.error.response.data.variants_not_sent.map((variant, index) => (
                                                <ListItem key={variant.id || index}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <ErrorOutline fontSize="small" color="error" />
                                                        <Typography variant="body2">
                                                            {variant.subject || variant.message?.subject || `Variant ${variant.variant_alpha || variant.id}`}
                                                        </Typography>
                                                    </Box>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                )}
                                
                                {sendTestMutation.error?.response?.data?.emails_invalid?.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Invalid Emails:</Typography>
                                        <List dense>
                                            {sendTestMutation.error.response.data.emails_invalid.map(email => (
                                                <ListItem key={email}>{email}</ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                )}
                                
                                {sendTestMutation.error?.response?.data?.emails_suppressed?.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Suppressed Emails:</Typography>
                                        <List dense>
                                            {sendTestMutation.error.response.data.emails_suppressed.map(email => (
                                                <ListItem key={email}>{email}</ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                )}
                                
                            </Alert>
                        </Box>
                    }

                    <Box sx={{ mt: 2, mb: 2, flexShrink: 0 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2 }}>Sending Test To:</Typography>
                        {emails.length === 0 && selectedAudiences.length === 0 ? (
                            <Typography color="text.secondary" sx={{ mb: 2 }}>No emails or audiences</Typography>
                        ) : (
                            <>
                                {emails.length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" color="text.secondary">Emails:</Typography>
                                        <List dense>
                                            {emails.map((email) => (
                                                <ListItem 
                                                    key={email}
                                                    secondaryAction={
                                                        <IconButton 
                                                            edge="end" 
                                                            size="small" 
                                                            onClick={() => removeEmail(email)}
                                                        >
                                                            <CloseTwoTone />
                                                        </IconButton>
                                                    }
                                                >
                                                    <Typography variant="body2">{email}</Typography>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                )}
                                {selectedAudiences.length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" color="text.secondary">Audiences:</Typography>
                                        <List dense>
                                            {selectedAudiences.map((audience) => (
                                                <ListItem 
                                                    key={audience.id}
                                                    secondaryAction={
                                                        <IconButton 
                                                            edge="end" 
                                                            size="small" 
                                                            onClick={() => setSelectedAudiences(prev => prev.filter(a => a.id !== audience.id))}
                                                        >
                                                            <CloseTwoTone />
                                                        </IconButton>
                                                    }
                                                >
                                                    <Typography variant="body2">{audience.name}</Typography>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                )}
                            </>
                        )}

                        {!showAddRecipients ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                <Button 
                                    variant="outlined" 
                                    onClick={() => setShowAddRecipients(true)}
                                >
                                    Add Recipients
                                </Button>
                            </Box>
                        ) : (
                            <Box sx={{ flexShrink: 0 }}>
                                <Typography variant="subtitle2" sx={{ mb: 2 }}>Add Recipients to Test:</Typography>
                                
                                <Typography component='div' variant='subtitle2'>Audiences</Typography>
                                {testAudiences && <AudienceSelector audiences={testAudiences} onChange={setSelectedAudiences} value={selectedAudiences} /> }

                                <Typography component='div' variant='subtitle2' mt={2}>Emails</Typography>
                                {error && <Typography sx={{color: '#870000'}} component='div' variant='subtitle2'>{error}</Typography>}
                                <Box sx={{ marginTop: 1, marginBottom: 2, display: 'flex'}}>
                                    <TextField sx={{width: 300}} size='small' value={currentEmail} onChange={(e) => setCurrentEmail(e.target.value)} label='Email address' />
                                    <Button className='new_primary_button' disabled={false} onClick={addEmail} style={{width: 'auto', height: '40px', marginLeft: 'auto' }}>
                                        Add Email
                                    </Button>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                    <Button 
                                        variant="contained" 
                                        onClick={() => setShowAddRecipients(false)}
                                    >
                                        Done Adding
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>

                    {message.editable_variants && message.editable_variants.some(variant => !variant.can_be_made_sendable) && (
                        <Alert severity="warning" sx={{ mb: 2, flexShrink: 0 }}>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                {message.editable_variants.filter(variant => !variant.can_be_made_sendable).length} variants are not yet sendable
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Check Overview and Schedule to identify and resolve the issues
                            </Typography>
                        </Alert>
                    )}

                    <Box sx={{ mb: 3, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                        <MutationLoadingButton
                            mutation={sendTestMutation}
                            sx={{width: '120px', height: '36px', fontSize: 14}} 
                            variant='contained' 
                            disabled={message.editable_variants && message.editable_variants.some(variant => !variant.can_be_made_sendable)}
                            onClick={sendTest}>
                            Send Test
                        </MutationLoadingButton>
                    </Box>

                    {/* Test Variants List */}
                    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, flexShrink: 0 }}>Previous Tests:</Typography>
                        {isLoadingVariants ? (
                            <Typography>Loading variants...</Typography>
                        ) : testVariants?.length > 0 ? (
                            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                                {testVariants.map((variant) => (
                                    <VariantTestCard key={variant.id} variant={variant} />
                                ))}
                            </Box>
                        ) : (
                            <Typography color="text.secondary">No test variants found</Typography>
                        )}
                    </Box>

                </Box>
            </Box>
        </Box>
    );
};

export default SendTestSidebar;
